import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { is3pProductKey, type ProductKeys } from '../../../../common/constants/products';
import { ResultsStates } from '../../../../common/constants/results';
import type { ThirdPartyConfigsWithAPIResponse } from '../../../../common/constants/schemas/3p-config';
import { AppFilters } from '../../../../common/ui/filters/app-filter-sidebar/app-filters';
import { NounFilters } from '../../../../common/ui/filters/app-filter-sidebar/noun-filters';
import { GiveFeedback } from '../../../../common/ui/give-feedback';
import { useAppFilterState, useHideSmartLinks } from '../../../../common/ui/right-panel';
import { onAppFilterShowHideClicked } from '../../../../common/utils/events/app-filter-show-hide-clicked';
import {
	serveRightRailRecommendationsImprovementsWithExposure,
	triggerExposureForRightRailImprovements,
} from '../../../../common/utils/experiments';
import { useSafeIntercom } from '../../../../common/utils/use-safe-intercom';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { useFireVisibleProductsToAuthShown } from '../../../../controllers/analytics/use-visible-products-to-auth-shown';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useSelectFilter } from '../../../../controllers/filters/base-select-filter';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import { useRightPanelRecommendations } from '../../../../controllers/recommened3pApps';
import { useApplicationMode, useBootstrap, useIsNounsAvailable } from '../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../controllers/store/bootstrap/accessors';
import { useProductResultCountMap, useResultsRequestId, useResultsState, useSearchActions, useSearchQuery, useSelectedDatasource, useSelectedProduct, useSelectedProducts } from '../../../../controllers/store/search';

import { messages } from './messages';

const AppFilter = ({
	top,
	isScrollable,
}: {
	top: number;
	isScrollable: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [
		{
			cloudId,
			experimentConfig,
			user,
			products: availableProducts,
			nouns: availableNouns,
			thirdPartyConfigs,
		},
	] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [productResultCountMap] = useProductResultCountMap();
	const [searchQuery] = useSearchQuery();
	const { selectProductFilter, unselectProductFilter, unsafe_setAtlasNouns, unsafe_setWebPageType } = useSearchActions();
	const intercom = useSafeIntercom();
	const [storedState, { setFilterHidden }] = useAppFilterState();
	useHideSmartLinks({ thirdPartyConfigs, storedState: storedState, setFilterHidden });
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProducts] = useSelectedProducts();
	const appMode = useApplicationMode();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [requestId] = useResultsRequestId();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();

	const [isNounsAvailable] = useIsNounsAvailable();

	const [universalTypeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });

	const appFilterSidebarStyles = useMemo(
		() =>
			xcss({
				overflowY: 'auto',
				overflowX: 'hidden',
				// https://hello.atlassian.net/wiki/x/tA0p-Q
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				height: `calc(100vh - ${top}px - 20px)`,
				padding: 'space.050',
				margin: 'space.negative.050',
				justifyContent: 'start',
			}),
		[top],
	);

	// Trigger an exposure once when the component is mounted.
	useEffect(() => {
		triggerExposureForRightRailImprovements();
	}, []);

	const { recommended3pApps } = useRightPanelRecommendations({
		userId: user?.id,
		showRecommendations: serveRightRailRecommendationsImprovementsWithExposure(),
	});
	const appFilterProps = useMemo(() => {
		const props = {
			searchQuery,
			thirdPartyConfigs,
			availableProducts,
			availableNouns,
			selectedProduct,
			primaryProduct,
			selectedDatasource,
			isNounsAvailable,
			filterData: universalTypeFilterData,
			setAtlasNoun: unsafe_setAtlasNouns,
			setWebPageType: unsafe_setWebPageType,
			onSelectProduct: (
				newProduct: ProductKeys | undefined,
				datasource: string | ThirdPartyConfigsWithAPIResponse | undefined,
			) => {
				if (!newProduct && selectedProduct) {
					unselectProductFilter();
					return;
				} else {
					selectProductFilter(newProduct, datasource);

					if (newProduct && is3pProductKey(newProduct)) {
						intercom?.trackEvent('product_filter_clicked_3p', {
							product_filter: newProduct,
						});
					} else {
						intercom?.trackEvent('product_filter_clicked', {
							product_filter: newProduct,
						});
					}
				}
			},
			recommended3pApps,
			onVisibleProductsToAuthShown: useFireVisibleProductsToAuthShown,
			productResultCountMap: productResultCountMap ?? {},
			top,
			sidebarStoredState: storedState,
			onFilterHide: (productKey: ProductKeys) => {
				setFilterHidden(productKey, true);
				fireAnalyticsEvent(
					onAppFilterShowHideClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						productKey,
						hidden: true,
					}),
				);
			},
			onFilterShow: (productKey: ProductKeys) => {
				setFilterHidden(productKey, false);
				fireAnalyticsEvent(
					onAppFilterShowHideClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						productKey,
						hidden: false,
					}),
				);
			},
			useOAuthHandlers,
			useIsUserOAuthed,
			ErrorBoundary,
			isInitialState: resultsState === ResultsStates.INITIAL,
		};
		return props;
	}, [
		availableProducts,
		availableNouns,
		selectedProduct,
		selectedDatasource,
		unselectProductFilter,
		selectProductFilter,
		intercom,
		isNounsAvailable,
		universalTypeFilterData,
		unsafe_setAtlasNouns,
		unsafe_setWebPageType,
		productResultCountMap,
		top,
		storedState,
		setFilterHidden,
		thirdPartyConfigs,
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		searchQuery,
		primaryProduct,
		resultsState,
		recommended3pApps,
	]);

	return (
		<Stack
			role="group"
			aria-label={formatMessage(messages.groupLabel)}
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
			xcss={[isScrollable && appFilterSidebarStyles]}
			testId="app-filter-sidebar"
		>
			<ErrorBoundary section="SearchPageAppFilter">
				<AppFilters {...appFilterProps} />
			</ErrorBoundary>
			<NounFilters
				{...appFilterProps}
				onSelectProduct={(newProduct: ProductKeys | undefined) => {
					if (!newProduct && selectedProduct) {
						unselectProductFilter();
						return;
					} else {
						selectProductFilter(newProduct);

						if (newProduct && is3pProductKey(newProduct)) {
							intercom?.trackEvent('product_filter_clicked_3p', {
								product_filter: newProduct,
							});
						} else {
							intercom?.trackEvent('product_filter_clicked', {
								product_filter: newProduct,
							});
						}
					}
				}}
			/>
			<GiveFeedback
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				experimentConfig={experimentConfig}
				selectedProducts={selectedProducts}
				user={user}
				appMode={appMode}
				requestId={requestId}
			/>
		</Stack>
	);
};

export default AppFilter;
